import { apiUrl } from '@monorepo/shared/apiClient/httpClient';
import { DocumentFileNameCell } from '@monorepo/shared/componentsV2/documents/DocumentFileNameCell';
import { FolderNameCell } from '@monorepo/shared/componentsV2/documents/FolderNameCell';
import {
  DocumentsTable,
  ExpandedFolderPlaceholderText,
} from '@monorepo/shared/componentsV2/documents/styled';
import {
  composeDocumentsFileRow,
  composeDocumentsFolderRow,
  getFileByDocumentsRowIdMap,
  getDocumentsRowCreatedAt,
  getDocumentsRowCreatedByName,
} from '@monorepo/shared/documents/composeDocumentsTableRows';
import {
  DOCUMENTS_TABLE_ROW_KIND,
  EXPANDED_FOLDER_PLACEHOLDER_LABELS,
} from '@monorepo/shared/documents/consts';
import {
  DocumentsFileRow,
  DocumentsFolderRow,
  DocumentsTableRow,
} from '@monorepo/shared/documents/types';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { Pagination } from '@monorepo/shared/componentsV2/Table/Pagination';
import { TableColumn } from '@monorepo/shared/componentsV2/Table/types';
import { useMovableTableRows } from '@monorepo/shared/componentsV2/Table/useMovableTableRows';
import { useInlineFolderExpansion } from '@monorepo/shared/hooks/documents/useInlineFolderExpansion';
import { useFrontendPagination } from '@monorepo/shared/hooks/useFrontendPagination';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import { fileTypeFromMime } from '@monorepo/shared/utils/documentFileType';
import { isOrganizationScopedFolder } from '@monorepo/shared/utils/documentFolderScope';
import { getDocumentUploadEmptyStateSecondaryText } from '@monorepo/shared/utils/documentUpload';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import { DocumentFolderScope } from 'mapistry-shared';
import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useSiteDocumentsList } from '../../hooks/useSiteDocumentsList';
import { useUpdateSiteDocument } from '../../hooks/useUpdateSiteDocument';
import { StyledMain } from '../styled';
import { SiteDocumentsTableActions } from '../SiteDocumentsTableActions';

const MoveOutDropZone = styled.div<{ $isActive: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  margin: 0.5rem;
  color: ${({ theme }) => theme.colors.blue64};
  background-color: ${({ theme, $isActive }) =>
    $isActive ? theme.colors.blue08 : 'transparent'};
  border: 2px dashed ${({ theme }) => theme.colors.blue40};
  border-radius: 4px;
`;

function getColumns(
  organizationId: string,
  projectId: string,
  canEditSiteDocuments: boolean,
  expandedFolderIds: Set<string>,
  toggleFolder: (folderId: string) => void,
): TableColumn<DocumentsTableRow>[] {
  return [
    {
      id: 'name',
      header: 'Name',
      contents: (row) => {
        if (row.kind === DOCUMENTS_TABLE_ROW_KIND.PLACEHOLDER) {
          return (
            <ExpandedFolderPlaceholderText>
              {EXPANDED_FOLDER_PLACEHOLDER_LABELS[row.placeholder]}
            </ExpandedFolderPlaceholderText>
          );
        }
        if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER) {
          return (
            <FolderNameCell
              name={row.folder.name}
              to={row.folder.id}
              isExpandable
              isExpanded={expandedFolderIds.has(row.folder.id)}
              onToggleExpand={() => toggleFolder(row.folder.id)}
              showOrganizationScopedIcon={isOrganizationScopedFolder(
                row.folder.scope,
              )}
            />
          );
        }
        return (
          <DocumentFileNameCell
            name={row.file.name}
            downloadUrl={`${apiUrl}${row.file.downloadUrl}`}
            indented={row.parentFolderId != null}
            isDraggable={canEditSiteDocuments && row.file.projectId != null}
          />
        );
      },
    },
    {
      id: 'type',
      header: 'Type',
      contents: (row) => {
        if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER) return 'Folder';
        if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FILE) {
          return fileTypeFromMime(row.file.mimeType, row.file.name);
        }
        return '';
      },
      align: 'center',
      width: '12%',
    },
    {
      id: 'fileCount',
      header: 'File Count',
      contents: (row) =>
        row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER
          ? String(row.folder.fileCount)
          : '',
      align: 'center',
      width: '10%',
    },
    {
      id: 'createdAt',
      header: 'Added At',
      contents: getDocumentsRowCreatedAt,
      align: 'center',
      width: '15%',
    },
    {
      id: 'createdBy',
      header: 'Created By',
      contents: getDocumentsRowCreatedByName,
      align: 'center',
      width: '15%',
    },
    {
      id: 'actions',
      header: 'Actions',
      contents: (row) =>
        row.kind === DOCUMENTS_TABLE_ROW_KIND.FILE ? (
          <SiteDocumentsTableActions
            organizationId={organizationId}
            projectId={projectId}
            document={row.file}
            canEditSiteDocuments={canEditSiteDocuments}
          />
        ) : (
          ''
        ),
      align: 'center',
      width: '10%',
    },
  ];
}

interface SiteDocumentsTableProps {
  organizationId: string;
  projectId: string;
  filter: string;
  canEditSiteDocuments: boolean;
}

export function SiteDocumentsTable({
  organizationId,
  projectId,
  filter,
  canEditSiteDocuments,
}: SiteDocumentsTableProps) {
  const { siteDocuments, isLoading } = useSiteDocumentsList({
    organizationId,
    projectId,
  });

  const {
    expandedFolderIds,
    folderIdsWithMatchingFiles,
    toggleFolder,
    composeDisplayRows,
  } = useInlineFolderExpansion({
    organizationId,
    projectId,
    files: siteDocuments?.files,
    searchTerm: filter,
  });

  const columns = useMemo(
    () =>
      getColumns(
        organizationId,
        projectId,
        canEditSiteDocuments,
        expandedFolderIds,
        toggleFolder,
      ),
    [
      canEditSiteDocuments,
      organizationId,
      projectId,
      expandedFolderIds,
      toggleFolder,
    ],
  );

  const topLevelRows: Array<DocumentsFolderRow | DocumentsFileRow> =
    useMemo(() => {
      if (!siteDocuments) return [];
      return [
        ...siteDocuments.files
          .filter((file) => file.folderId == null)
          .map((file) => composeDocumentsFileRow(file)),
        ...siteDocuments.folders.map(composeDocumentsFolderRow),
      ];
    }, [siteDocuments]);

  const filterRowsBy = useCallback(
    (row: DocumentsFolderRow | DocumentsFileRow) => {
      if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER) {
        return (
          folderIdsWithMatchingFiles.has(row.id) ||
          lookFor(filter, row.folder, ['name'])
        );
      }
      return lookFor(filter, row.file, ['name']);
    },
    [filter, folderIdsWithMatchingFiles],
  );

  const { page, setPage, filteredResults, rows, rowsPerPage } =
    useFrontendPagination({
      items: topLevelRows,
      filterRowsBy,
      resetPageWhen: [filter, organizationId, projectId],
    });

  const displayRows = useMemo(
    () => composeDisplayRows(rows),
    [composeDisplayRows, rows],
  );

  const { showUserFriendlyErrorToast, success } = useToast();
  const [moveDocument] = useUpdateSiteDocument({
    config: { throwOnError: true },
  });

  const fileByRowId = useMemo(
    () => getFileByDocumentsRowIdMap(displayRows),
    [displayRows],
  );

  const handleMoveFile = useCallback(
    async (rowId: string, folderId: string | null) => {
      const file = fileByRowId.get(rowId);
      if (!file || file.folderId === folderId) return;
      try {
        await moveDocument({
          organizationId,
          projectId,
          documentId: file.id,
          document: { folderId },
        });
        success('Document was moved.');
      } catch (err) {
        showUserFriendlyErrorToast(err, 'Unable to move document.');
      }
    },
    [
      fileByRowId,
      moveDocument,
      organizationId,
      projectId,
      showUserFriendlyErrorToast,
      success,
    ],
  );

  const isRowDraggable = useCallback(
    (row: DocumentsTableRow) =>
      canEditSiteDocuments &&
      row.kind === DOCUMENTS_TABLE_ROW_KIND.FILE &&
      row.file.projectId != null,
    [canEditSiteDocuments],
  );

  const isRowDropTarget = useCallback(
    (row: DocumentsTableRow, draggedRowId: string) =>
      row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER &&
      row.folder.scope === DocumentFolderScope.PROJECT &&
      fileByRowId.get(draggedRowId)?.folderId !== row.id,
    [fileByRowId],
  );

  const { getMovableRowProps, getDropZoneProps, draggedRowId, isDragging } =
    useMovableTableRows<DocumentsTableRow>({
      isRowDraggable,
      isRowDropTarget,
      onMove: handleMoveFile,
    });

  const showMoveOutDropZone =
    isDragging &&
    draggedRowId != null &&
    fileByRowId.get(draggedRowId)?.folderId != null;
  const { isActive: isMoveOutActive, ...moveOutHandlers } =
    getDropZoneProps(null);

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  if (isLoading) {
    return <Loading />;
  }

  if (!topLevelRows.length) {
    return (
      <StyledMain>
        <EmptyState
          primaryText="There are no documents for this site yet."
          secondaryText={
            canEditSiteDocuments
              ? getDocumentUploadEmptyStateSecondaryText({
                  uploadButtonLabel: 'Add document',
                  suffix: ', or open a folder to add documents there.',
                })
              : 'Your organization or site admin can add documents to this site.'
          }
        />
      </StyledMain>
    );
  }

  return (
    <StyledMain>
      <SkipLink linkText="Skip to pagination" />
      <DocumentsTable
        ariaLabel="Site documents"
        columns={columns}
        rows={displayRows}
        getRowProps={getMovableRowProps}
        dummyRow={
          showMoveOutDropZone ? (
            <MoveOutDropZone $isActive={isMoveOutActive} {...moveOutHandlers}>
              Drop here to move out of the folder (top level)
            </MoveOutDropZone>
          ) : undefined
        }
        isPrefilteredAndNotEmpty
        makeTableFocusable
      />
      <SkipLinkTarget />
      <Pagination
        totalRowCount={filteredResults.length}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
      />
    </StyledMain>
  );
}
